import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { 
  Mail,
  Copy,
  Edit,
  ExternalLink,
  X
} from 'lucide-react';

interface EmailDraftData {
  subject: string;
  body: string;
  customer_name: string;
  email_type: string;
  draft_id?: string;
}

interface EmailDraftModalProps {
  isOpen: boolean;
  onClose: () => void;
  emailData: EmailDraftData | null;
}

export const EmailDraftModal: React.FC<EmailDraftModalProps> = ({
  isOpen,
  onClose,
  emailData,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedSubject, setEditedSubject] = useState('');
  const [editedBody, setEditedBody] = useState('');

  React.useEffect(() => {
    if (emailData) {
      setEditedSubject(emailData.subject);
      setEditedBody(emailData.body);
    }
  }, [emailData]);

  const copyToClipboard = () => {
    if (!emailData) return;
    const text = `Subject: ${editedSubject}\n\n${editedBody}`;
    navigator.clipboard.writeText(text);
  };

  const openInEmailClient = () => {
    const subject = encodeURIComponent(editedSubject);
    const body = encodeURIComponent(editedBody);
    window.open(`mailto:?subject=${subject}&body=${body}`);
  };

  if (!isOpen || !emailData) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-2xl border-0 bg-card/95 backdrop-blur-md">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-6 border-b border-border">
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-2">
              <Mail className="h-6 w-6 text-blue-500" />
              <CardTitle className="text-2xl font-bold text-foreground">Email Draft</CardTitle>
            </div>
            <p className="text-sm text-muted-foreground">
              {emailData.email_type.charAt(0).toUpperCase() + emailData.email_type.slice(1)} email for {emailData.customer_name}
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsEditing(!isEditing)}
            >
              <Edit className="h-4 w-4 mr-2" />
              {isEditing ? 'Preview' : 'Edit'}
            </Button>
            <Button variant="ghost" size="sm" onClick={onClose} className="hover:bg-muted rounded-full">
              <X className="h-5 w-5" />
            </Button>
          </div>
        </CardHeader>

        <CardContent className="pt-6">
          <div className="space-y-6">
            {/* Subject */}
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">Subject</label>
              {isEditing ? (
                <input
                  type="text"
                  value={editedSubject}
                  onChange={(e) => setEditedSubject(e.target.value)}
                  className="w-full px-3 py-2 border border-input rounded-md focus:ring-2 focus:ring-primary focus:border-transparent bg-background text-foreground"
                />
              ) : (
                <div className="bg-muted rounded-lg p-3 text-foreground font-medium">
                  {editedSubject}
                </div>
              )}
            </div>

            {/* Body */}
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">Body</label>
              {isEditing ? (
                <textarea
                  value={editedBody}
                  onChange={(e) => setEditedBody(e.target.value)}
                  className="w-full min-h-[300px] px-3 py-2 border border-input rounded-md focus:ring-2 focus:ring-primary focus:border-transparent bg-background text-foreground font-mono text-sm"
                />
              ) : (
                <div className="bg-muted border border-border rounded-lg p-4 whitespace-pre-wrap text-foreground min-h-[300px]">
                  {editedBody}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex justify-between items-center pt-4 border-t border-border">
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={copyToClipboard}
                  className="flex items-center space-x-2"
                >
                  <Copy className="h-4 w-4" />
                  <span>Copy</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={openInEmailClient}
                  className="flex items-center space-x-2"
                >
                  <ExternalLink className="h-4 w-4" />
                  <span>Open in Email</span>
                </Button>
              </div>
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

